import { useNavigate } from 'react-router-dom'
import { Text } from '@mantine/core'
import { CountTile, CountTileGrid, SectionAction, SectionCard, Skeleton } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import {
  countOrDash, findingCount, governanceQueuePath, inactivityWithheldNotice, isMigrationDependent,
  previewAccess, previewFetchEnabled,
} from './governanceFormat'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import { useGovernanceFinding } from '../../hooks/useGovernance'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { NOT_MEASURED, errorKind } from '../../utils/format'
import type { GovernanceFinding, GovernanceOverview } from '../../types/governance'

const SURFACE = 'Governance Queue'

/**
 * The Governance Queue on the Overview: seven counts, each its own request, and a
 * way into the page. The Overview mounts it (wave 3); nothing here is a fixture.
 *
 * FETCHES ONLY ON `admin` (hooks/useLighthouseAdmin.ts): a preview never polls an
 * endpoint the caller may not read. `denied` is the compact restricted state;
 * `unknown` is a skeleton while the job catalogue answers, then "Could not
 * determine access" — never a guess in either direction.
 *
 * NO SUBTITLE SUMS THE SEVEN. They are different kinds of thing — flagged reports,
 * grant rows of two mechanisms, view-only grants on archived reports, reports,
 * schedules, people — and lapsed grants confer nothing at all, so a total would
 * count inert rows as work and add people to reports. The old fixture's "N items
 * require attention" is exactly that sum, and it is not carried over.
 */
export function GovernanceQueuePreview() {
  const navigate = useNavigate()
  const gate = useLighthouseAdmin()
  const catalogue = useEtlJobCatalog()
  const enabled = previewFetchEnabled(gate)
  const access = previewAccess(gate, { isError: catalogue.isError, isLoaded: catalogue.data !== undefined })

  return (
    <SectionCard
      title="Governance Queue"
      subtitle="Access nobody is watching — read-only"
      flush
      action={access === 'restricted'
        ? undefined
        : <SectionAction onClick={() => navigate(governanceQueuePath())}>View all →</SectionAction>}
    >
      {access === 'restricted' ? (
        <div style={{ padding: 12 }}><RestrictedState compact surface={SURFACE} /></div>
      ) : access === 'checking' ? (
        <div style={{ padding: 12 }}><Skeleton height={92} /></div>
      ) : access === 'undetermined' ? (
        <div style={{ padding: 12 }}>
          <Text size="sm" c="dimmed">
            Could not determine access. The job catalogue, which carries the server&rsquo;s answer on
            Lighthouse.Admin, did not say — so no count is shown rather than a guess.
          </Text>
        </div>
      ) : (
        <CountTileGrid>
          <FindingTile finding="ownership" enabled={enabled} label={() => 'Ownership gaps'}
            hint={() => 'Flagged reports with no accountable owner'} />
          <FindingTile finding="expiring" enabled={enabled}
            label={(d) => `Expiring ≤ ${d?.expiringWithinDays ?? NOT_MEASURED}d`}
            hint={() => 'Live access about to be lost'} />
          <FindingTile finding="lapsed" enabled={enabled} label={() => 'Lapsed (inert)'}
            hint={() => 'Confer nothing — housekeeping only'} />
          <FindingTile finding="archived" enabled={enabled} label={() => 'On archived reports (view-only)'}
            hint={() => 'Never counted as live; run returns on restore'} />
          {/* The PEOPLE figure (findingCount): a report reachable only through group grants reaches nobody. */}
          <FindingTile finding="reach" enabled={enabled} label={() => 'Shared with people'}
            hint={(d) => `of ${countOrDash(d?.reportsExamined)} reports examined`} />
          <FindingTile finding="schedules" enabled={enabled} label={() => 'Schedules as non-owner'}
            hint={() => 'Delivering under an unaccountable identity'} />
          <FindingTile finding="inactive" enabled={enabled} label={() => 'Inactive holders'}
            hint={(d) => (d && inactivityWithheldNotice(d)
              ? 'Withheld — the run log does not cover the window'
              : `Ran nothing in ${d?.inactivityThresholdDays ?? NOT_MEASURED} days`)} />
        </CountTileGrid>
      )}
    </SectionCard>
  )
}

/**
 * One tile, one request. Its own skeleton and its own error, so a missing
 * migration darkens the five groups that need it and leaves the other two.
 * A null count — not computed, or withheld — is a dash, never 0.
 */
function FindingTile({
  finding, enabled, label, hint,
}: {
  finding: GovernanceFinding
  enabled: boolean
  label: (data: GovernanceOverview | undefined) => string
  hint: (data: GovernanceOverview | undefined) => string
}) {
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useGovernanceFinding(finding, { enabled })
  const review = (
    <SectionAction onClick={() => navigate(governanceQueuePath(finding))}>Review →</SectionAction>
  )

  if (isError) {
    const kind = errorKind(error)
    const why = kind === 'forbidden'
      ? 'Refused — needs Lighthouse.Admin'
      : kind === 'server' && isMigrationDependent(finding)
        ? 'Could not load — AddGrantExpiryAndNotes may not be applied'
        : 'Could not load'
    return <CountTile value={NOT_MEASURED} label={label(undefined)} hint={why} action={review} />
  }

  // `|| !data`: between retries the flags read "loaded, no error" with no payload,
  // and a tile rendered then would print a dash that looks like a measured blank.
  if (isLoading || !data) {
    return <CountTile value="" label={label(undefined)} hint={<Skeleton width={64} height={20} />} />
  }

  return (
    <CountTile
      value={countOrDash(findingCount(finding, data))}
      label={label(data)}
      hint={hint(data)}
      action={review}
    />
  )
}
